import { Campaign as ApiCampaign } from "./api";

// Legacy Campaign interface for backward compatibility with charts
export interface LegacyCampaign {
  id: string;
  batchCode: string;
  name: string;
  product: string;
  productQuantity: number;
  status: "active" | "inactive" | "ended";
  startDate: string;
  endDate: string;
  exchangeRate: string;
  totalCodes: number;
  usedCodes: number;
  remainingCodes: number;
  description?: string;
  webLink?: string;
}

// Adapter functions to convert API Campaign to Legacy Campaign
export class CampaignAdapter {
  // Convert API Campaign to Legacy Campaign
  static toLegacy(apiCampaign: ApiCampaign): LegacyCampaign {
    // Calculate total codes based on the formula: (total_gift * rate_code) / rate_gift
    const totalCodes = Math.floor(
      (apiCampaign.total_gift * apiCampaign.rate_code) / apiCampaign.rate_gift
    );

    // Use num_code_used from API response
    const usedCodes = apiCampaign.num_code_used || 0;
    const remainingCodes = totalCodes - usedCodes;

    // Convert status from number to string, considering end date
    const now = new Date();
    const endDate = new Date(apiCampaign.end_date);
    const startDate = new Date(apiCampaign.start_date);
    const isExpired = endDate < now;
    const isNotStarted = startDate > now;

    // Debug: Log date comparison details
    console.log(`Date comparison for ${apiCampaign.name}:`, {
      now: now.toISOString(),
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      isNotStarted,
      isExpired,
      isInActivePeriod: !isNotStarted && !isExpired,
    });

    let status: "active" | "inactive" | "ended";
    if (isExpired) {
      status = "ended";
    } else if (isNotStarted) {
      status = "inactive"; // Chưa bắt đầu
    } else {
      // Trong khoảng thời gian hoạt động: kiểm tra status từ API
      // status = 1 là active (bật), 2 là inactive (tắt)
      if (apiCampaign.status === 1) {
        status = "active";
      } else {
        status = "inactive";
      }
    }

    // Debug logging for status conversion
    console.log(`Campaign ${apiCampaign.name}:`, {
      startDate: apiCampaign.start_date,
      endDate: apiCampaign.end_date,
      apiStatus: apiCampaign.status,
      isExpired,
      isNotStarted,
      finalStatus: status,
      now: now.toISOString(),
      endDateISO: endDate.toISOString(),
      startDateISO: startDate.toISOString(),
    });

    // Create exchange rate string
    const exchangeRate = `${apiCampaign.rate_gift} quà : ${apiCampaign.rate_code} code`;

    return {
      id: apiCampaign._id || `campaign-${Date.now()}`,
      batchCode: apiCampaign.batch,
      name: apiCampaign.name,
      product: apiCampaign.product_name,
      productQuantity: apiCampaign.total_gift,
      status: status as "active" | "inactive" | "ended",
      startDate: apiCampaign.start_date,
      endDate: apiCampaign.end_date,
      exchangeRate: exchangeRate,
      totalCodes: totalCodes,
      usedCodes: usedCodes,
      remainingCodes: remainingCodes,
      description: apiCampaign.description,
      webLink: apiCampaign.landing_page_link,
    };
  }

  // Convert array of API Campaigns to Legacy Campaigns
  static toLegacyArray(apiCampaigns: ApiCampaign[]): LegacyCampaign[] {
    return apiCampaigns.map((campaign) => this.toLegacy(campaign));
  }

  // Convert Legacy Campaign to API Campaign (for updates)
  static toApi(legacyCampaign: LegacyCampaign): Partial<ApiCampaign> {
    // Parse exchange rate to get rate_gift and rate_code
    const exchangeRateMatch = legacyCampaign.exchangeRate.match(
      /(\d+)\s*quà\s*:\s*(\d+)\s*code/
    );
    const rateGift = exchangeRateMatch ? parseInt(exchangeRateMatch[1]) : 1;
    const rateCode = exchangeRateMatch ? parseInt(exchangeRateMatch[2]) : 1;

    return {
      _id: legacyCampaign.id,
      name: legacyCampaign.name,
      description: legacyCampaign.description || "",
      product_name: legacyCampaign.product,
      gift_name: legacyCampaign.product, // Assuming gift_name is same as product
      total_gift: legacyCampaign.productQuantity,
      rate_code: rateCode,
      rate_gift: rateGift,
      start_date: legacyCampaign.startDate,
      end_date: legacyCampaign.endDate,
      landing_page_link: legacyCampaign.webLink || "",
      batch: legacyCampaign.batchCode,
      api_key: "", // This would need to be provided separately
      status: legacyCampaign.status === "active" ? 1 : 2,
    };
  }
}

// Export types
export type { ApiCampaign };
